import { useContext, useEffect, useState, useRef, useCallback } from "react";
import {
    Box,
    Chip,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { FiltersContext } from "../store/filters-options-context";
import ComboFilters from "../components/SelectBoxFilter";
import imgSdg from "../assets/sdg-color.png";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import InsightsIcon from "@mui/icons-material/Insights";
import SourceSharpIcon from "@mui/icons-material/SourceSharp";
import DownloadIcon from '@mui/icons-material/Download';
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";
import styled from "@emotion/styled";
import AutocompleteComponent from "./SearchBox";
import BulkFilters from "../components/BulkSidebar";
import SearchCountry from "../components/BulkCountries";

const MobileToggleIcon = styled(IconButton)(({ theme }) => ({
    position: "fixed",
    top: "9px",
    left: "6px",
    zIndex: 1000,
    display: "block",
    [theme.breakpoints.up("sm")]: {
        top: '17px',
        left: "18px",
    },
    [theme.breakpoints.up("md")]: {
        display: "none",
    },
}));

export default function Sidebar() {
    const {
        selectOptions,
        handleChange,
        subCategory,
        setSubCategory,
        handleRoute,
        itemsList,
        valueSelected,
        setValueSelected,
        setMainCategory,
        mainCategory,
        activeMenu,
    } = useContext(FiltersContext);
    const [displayedItems, setDisplayedItems] = useState([]);
    let { section, subCat, countryId, years } = useParams();
    const theme = useTheme();

    const location = useLocation();
    const navigate = useNavigate();
    // Derive type supporting both '/indicators', '/bulk', and '/files/:type/...'
    const pathParts = location.pathname.split('/').filter(Boolean);
    const firstSeg = (pathParts[0] || '').toLowerCase();
    const secondSeg = (pathParts[1] || '').toLowerCase();
    const type = (firstSeg === 'files' ? secondSeg : firstSeg) || undefined;
    const indicatorsRef = useRef(null);

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [isMenuOpen, setIsMenuOpen] = useState(!isMobile);
    
    // Prevent navigation loops by tracking user intent vs automatic state updates
    const toggleAside = (newOpen) => () => {
        setIsMenuOpen(newOpen);
    };

    const updateUrlWithAnchor = useCallback((anchor) => {
        // Route is indicators/:section/:subCat/:countryId/:years/:scroll? - section/
        // subCat/countryId/years are read via useParams() above, and only mean this
        // on the indicators route (other routes reuse these param names differently).
        if (type !== 'indicators' || !countryId || !years) return;

        const scrollSegment = (anchor == null || anchor === '') ? null : encodeURIComponent(String(anchor));
        const pathSegments = ['indicators', section, subCat, encodeURIComponent(countryId), encodeURIComponent(years)];
        if (scrollSegment) pathSegments.push(scrollSegment);
        const newPath = `/${pathSegments.join('/')}`;

        if (newPath !== location.pathname) {
            // { replace: true } avoids adding a new browser-history entry per anchor change
            navigate(newPath, { replace: true });
        }
    }, [type, section, subCat, countryId, years, location.pathname, navigate]);

    const handleScrollToElement = useCallback((id, shouldCloseSidebar = true, isInitialLoad = false) => {
        if (shouldCloseSidebar) {
            toggleAside(false)();
        }
        const container = document.getElementById("scroll-content");
        const element = document.getElementById(id);

        if (element) {
            let scrollOffset;
            
            if (isInitialLoad) {
                // For initial load, use offsetTop for accurate positioning
                const elementOffsetTop = element.offsetTop;
                scrollOffset = Math.max(0, elementOffsetTop - 50);
                container.scrollTop = scrollOffset;
            } else {
                // For user interactions, calculate relative to current position
                const containerTop = container.getBoundingClientRect().top;
                const elementTop = element.getBoundingClientRect().top - 50;
                scrollOffset = container.scrollTop + elementTop - containerTop;
                
                container.scrollTo({
                    top: scrollOffset,
                    behavior: "smooth",
                });
            }
        }

        if (!isInitialLoad) {
            updateUrlWithAnchor(id);
        }
    }, [updateUrlWithAnchor]);


    

    const getUniqueItems = (items) => {
        // Deduplicate by stable id when available; fallback to label as a last resort.
        const seen = new Set();
        return items.filter((item) => {
            const key = item?.id ?? item?.label;
            if (key == null) return true; // keep if no stable key
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    };



    const showIndicators = useCallback((items, slug, wasClicked) => {
        setSubCategory(slug);

        const uniqueItems = getUniqueItems(items);
        setDisplayedItems(uniqueItems);

        // Only scroll to top if there's no scroll-anchor segment (or it's '0') to preserve anchor
        // scrolling. Read the live hash directly (not useParams()) so this callback's identity
        // stays stable across scroll ticks - depending on the constantly-updating scroll segment
        // would refire the effect that calls this and fight with the scroll restoration below.
        const hashPath = window.location.hash.replace(/^#/, '');
        const segments = hashPath.split('/').filter(Boolean);
        const trailingAnchor = segments[0] === 'indicators' ? segments[5] : null;

        if (!trailingAnchor || trailingAnchor === '0') {
            const sc = document.getElementById("scroll-content");
            if (sc) {
                sc.scrollTo({ top: 0, behavior: "auto" });
            }
        }
    }, [setSubCategory]);

    useEffect(() => {
        if (itemsList.length > 0) {
            const matchingItem = itemsList.find((item) => item.slug === subCat);

            if (matchingItem) {
                setSubCategory(subCat); // update selected slug
                showIndicators(matchingItem.items, matchingItem.slug, false);
            } else if (itemsList.length > 0) {
                const firstItem = itemsList[0];
                setSubCategory(firstItem.slug);
                showIndicators(firstItem.items, firstItem.slug, false); // fallback to first item only if no match
            }
        }
    }, [itemsList, subCat, setSubCategory, showIndicators]);

    useEffect(() => {
        // Only set mainCategory if it's different to prevent loops
        if (type && mainCategory !== type) {
            setMainCategory(type);
        }
        if (subCat) setSubCategory(subCat); // only update if subCat is defined
    }, [type, subCat, mainCategory, setMainCategory, setSubCategory]);

    // Remove automatic handleRoute calls that cause navigation loops
    // Navigation will only happen through explicit user actions like handleBoxClick

    useEffect(() => {
        setSubCategory(subCat);
    }, [subCat, setSubCategory]);

    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (type === "indicators") {
            setActiveIndex(0);
        } else if (type === "bulk") {
            setActiveIndex(2);
        } else {
            setActiveIndex(1);
        }
    }, [type]);

    // auto scroll sidebar when item list is hidden
    useEffect(() => {
        const container = indicatorsRef.current;
        if (!container) return;

        const activeItem = container.querySelector('.active');
        if (activeItem) {
            activeItem.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    }, [activeMenu]);

    // Sync the scroll-anchor segment in the URL when the active chart changes (mouse/keyboard scroll)
    useEffect(() => {
        if (activeMenu == null || String(activeMenu) === '') return;
        updateUrlWithAnchor(activeMenu);
    }, [activeMenu, updateUrlWithAnchor]);


    const boxes = [
        {
            label: "Indicators",
            category: "indicators",
            icon: <InsightsIcon sx={{ fontSize: isMobile ? "14px" : "16px" }} />,
        },
        {
            label: "Repository",
            category: "repository",
            icon: <SourceSharpIcon sx={{ fontSize: isMobile ? "14px" : "16px" }} />,
        },
        {
            label: "Bulk Data",
            category: "bulk",
            icon: <DownloadIcon sx={{ fontSize: isMobile ? "14px" : "16px" }} />,
        },
    ];

    const handleBoxClick = (index, category) => {
        setActiveIndex(index);
        setMainCategory(category);
        // Only navigate if user actually wants to change category and has required data
        if (valueSelected.area && (category === 'bulk' || valueSelected.domain_code || valueSelected.repositories)) {
            handleRoute(category);
        }
    };

    useEffect(() => {
        // Automatically select sane defaults when options are first available.
        if (!valueSelected.domain_code && selectOptions.domain_code.length > 0) {
            setValueSelected((prevValues) => ({
                ...prevValues,
                domain_code: selectOptions.domain_code[0],
            }));
        }

        // Only default the theme if there isn't a theme in the URL (subCat) and nothing selected yet.
        // Avoid forcing an indicator list that overrides URL-driven selection.
        if (!subCat && !valueSelected.themes && selectOptions.themes.length > 0) {
            const firstTheme = selectOptions.themes[0];
            setValueSelected((prevValues) => ({
                ...prevValues,
                themes: firstTheme,
            }));
            // Mirror handleChange's "themes" branch: selecting a theme sets subCategory,
            // which handleRoute needs to build the indicators URL. Without this, switching
            // to Indicators before ever touching the theme dropdown left subCategory unset
            // and silently blocked navigation.
            if (!subCategory && firstTheme?.slug) {
                setSubCategory(firstTheme.slug);
            }
        }

        if (!valueSelected.repositories && selectOptions.repositories.length > 0) {
            setValueSelected((prevValues) => ({
                ...prevValues,
                repositories: selectOptions.repositories[0],
            }));
        }
    // Do not call showIndicators here. let the [itemsList, subCat] effect drive the list to prevent state races.
    }, [
        selectOptions.domain_code,
        selectOptions.repositories,
        selectOptions.themes,
        subCat,
        subCategory,
        valueSelected.domain_code,
        valueSelected.repositories,
        valueSelected.themes,
        setValueSelected,
        setSubCategory,
    ]);


    return (
        <>
            <MobileToggleIcon 
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsMenuOpen(!isMenuOpen);
                }}
            >
                {isMenuOpen ? (
                    <CloseIcon sx={{ position: "absolute", left: "100%" }} />
                ) : (
                    <MenuIcon />
                )}
            </MobileToggleIcon>
            <Drawer
                open={isMenuOpen}
                variant={isMobile ? "temporary" : "permanent"}
                onClose={() => setIsMenuOpen(false)}
                position="relative"
                sx={{
                    "& .MuiDrawer-paper": {
                        position: isMobile ? "fixed" : "relative",
                        backgroundColor: isMobile
                            ? theme.palette.mode === "light"
                                ? "white"
                                : theme.palette.mode === "dark"
                                    ? "black"
                                    : "inherit"
                            : "inherit",
                        border: "none",
                    },
                }}
            >
                <aside>
                    {isMobile && location.pathname !== "/" && (
                        <Box>
                            <Box
                                component={Link}
                                to="/"
                                sx={{
                                    m: "10px 3px 2px 5px",
                                    lineHeight: "2rem",
                                    letterSpacing: "-0.1pt",
                                    color: theme.palette.primary.main,
                                    textDecoration: "none",
                                    fontWeight: "bold",
                                    display: "flex",
                                }}
                            >
                                THE STATE OF AGRIFOOD SYSTEMS
                            </Box>
                        </Box>
                    )}
                    <Box className="combo-container">
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={{ mt: isMobile ? 1 : 1.8, mb: isMobile ? 3 : 1 }}
                        >
                            <Box
                                display="flex"
                                backgroundColor={
                                    theme.palette.mode !== "dark"
                                        ? "rgba(0, 0, 0, 0.08)"
                                        : "#383838"
                                }
                                borderRadius="20px"
                                alignItems="center"
                                justifyContent="center"
                                position="relative"
                                flex="5"
                                sx={{ padding: "11px 0" }}
                            >
                                <Box
                                    position="absolute"
                                    top="11%"
                                    bottom="0"
                                    left={`${(100 / boxes.length) * activeIndex}%`}
                                    width={`${92 / boxes.length}%`}
                                    height="75%"
                                    margin="0 1.5%"
                                    backgroundColor={theme.palette.primary.main}
                                    borderRadius="15px"
                                    transition="all 0.3s ease"
                                    zIndex={1}
                                    sx={{ boxShadow: 1 }}
                                />

                                {/* Content Boxes */}
                                {boxes.map((box, index) => (
                                    <Box
                                        key={index}
                                        flex="1"
                                        display="flex"
                                        justifyContent="center"
                                        alignItems="center"
                                        position="relative"
                                        zIndex={2}
                                        cursor="pointer"
                                        sx={{
                                            cursor: "pointer",
                                            color: activeIndex === index ? "white" : "inherit",
                                            fontSize: { xs: "11px", lg: "11px", xl: "12px" },
                                            fontWeight: { xs: "500", lg: "normal" },
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleBoxClick(index, box.category);
                                        }}
                                    >
                                        {box.icon}&nbsp;&nbsp;{box.label}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        {isMobile && type === "indicators" && (
                            <Box sx={{ mb: "5px", mt: 2 }}>
                                <AutocompleteComponent
                                    onCloseDrawer={() => setIsMenuOpen(false)}
                                />
                            </Box>
                        )}
                        {type === "bulk" && <SearchCountry />}
                        {type !== "bulk" && (
                            <ComboFilters
                                selectOptions={selectOptions["area"]}
                                selectLabel="Select a Country"
                                selectId="area"
                                onChange={handleChange}
                                urlArea={valueSelected?.area?.id}
                            />
                        )}
                        {type === "indicators" && (
                            <>
                                <ComboFilters
                                    selectOptions={selectOptions["domain_code"]}
                                    selectLabel="Select an Outcome"
                                    selectId="domain_code"
                                    onChange={handleChange}
                                    urlDomain={section}
                                />
                                <ComboFilters
                                    selectLabel="Select a Theme"
                                    selectId="themes"
                                    onChange={handleChange}
                                    showIndicators={showIndicators}
                                    urlTheme={subCat}
                                />
                            </>
                        )}
                        {type === "repository" && (
                            <ComboFilters
                                selectOptions={selectOptions["repositories"]}
                                selectId="repositories"
                                onChange={handleChange}
                                selectLabel="Select a category from the repository"
                                urlDomain={section}
                            />
                        )}
                    </Box>
                    {type === "indicators" && (
                        <div className="indicators-section">
                            <Divider
                                sx={{
                                    margin: "10px 0 13px 0",
                                    fontWeight: "bold",
                                    flexShrink: 0,
                                }}
                            >
                                <Chip label="Indicators List" size="small" />
                            </Divider>
                            <List className="indicators" ref={indicatorsRef}>
                                {displayedItems.map((item) => (
                                    <ListItem
                                        key={item.id ?? item.label}
                                        onClick={() => handleScrollToElement(item.id)}
                                        className={`${Number(item.flag_sdg) === 1 ? "is-sdg" : ""
                                            } ${String(activeMenu) === String(item.id) ? "active" : ""}`}
                                        sx={{
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            transition: "background-color 0.2s ease-in-out",
                                            backgroundColor:
                                                String(activeMenu) === String(item.id)
                                                    ? theme.palette.primary.light
                                                    : "",
                                            border:
                                                String(activeMenu) === String(item.id)
                                                    ? `1px solid ${theme.palette.primary.light}`
                                                    : "1px solid transparent",
                                            "&:hover": {
                                                backgroundColor: theme.palette.secondary.dark,
                                            },
                                        }}
                                    >
                                        {Number(item.flag_sdg) === 1 ? (
                                            <img width="12px" src={imgSdg} alt="SDG indicator" />
                                        ) : null}
                                        <span>{item.label}</span>
                                    </ListItem>
                                ))}
                            </List>
                        </div>
                    )}
                    {type === "bulk" && <BulkFilters />}
                </aside>
            </Drawer>
        </>
    );
}
